import React, { useState } from 'react'
import { View } from 'react-native'

import LockIcon from '@common/assets/svg/LockIcon'
import Button from '@common/components/Button'
import DialogButton from '@common/components/Dialog/DialogButton'
import DialogFooter from '@common/components/Dialog/DialogFooter'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'

interface Props {
  onImport: () => void
  onClose: () => void
}

// Thin presentational body for the "Forgot password" reset flow. Two stages in a
// single sheet: (1) explain the consequence, (2) a hard confirm before the
// irreversible wipe runs. The confirm stage mirrors the design-system `Dialog`
// (left-aligned title, plain body, justifyEnd footer with a `danger` action).
const ForgotPasswordModal: React.FC<Props> = ({ onImport, onClose }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const [showConfirm, setShowConfirm] = useState(false)

  // Reset the stage when the sheet is dismissed so it reopens on the intro view.
  const handleClose = () => {
    setShowConfirm(false)
    onClose()
  }

  if (showConfirm) {
    return (
      <View>
        <Text fontSize={18} weight="semiBold" style={spacings.mbMi}>
          {t('Reset wallet?')}
        </Text>
        <Text fontSize={14} appearance="secondaryText" style={spacings.mb}>
          {t(
            'This will remove all wallet data from this device. You can restore your wallet anytime using your Secret Recovery Phrase. Make sure you’ve backed it up before continuing.'
          )}
        </Text>
        <DialogFooter horizontalAlignment="justifyEnd">
          <DialogButton text={t('Cancel')} type="secondary" onPress={() => setShowConfirm(false)} />
          <DialogButton
            style={spacings.ml}
            text={t('Reset')}
            testID="button-forgot-password-confirm"
            type="danger"
            onPress={onImport}
          />
        </DialogFooter>
      </View>
    )
  }

  return (
    <View style={[flexbox.alignCenter, spacings.ph, spacings.pvTy]}>
      <LockIcon width={44} height={44} color={theme.iconPrimary} style={spacings.mbLg} />
      <Text
        weight="medium"
        fontSize={20}
        style={[{ textAlign: 'center' }, spacings.mbSm, spacings.mtSm]}
      >
        {t('Forgot your password?')}
      </Text>
      <Text
        appearance="secondaryText"
        fontSize={14}
        style={[{ textAlign: 'center' }, spacings.mbLg]}
      >
        {t(
          'Your password cannot be recovered. The only way back in is to reset this app — this deletes all wallet information stored on this device (accounts, keys and private data) — then import your wallet again using your Secret Recovery Phrase and set a new password.'
        )}
      </Text>
      <Button
        testID="button-forgot-password-import"
        type="primary"
        size="large"
        text={t('Reset')}
        hasBottomSpacing={false}
        style={{ width: '100%' }}
        onPress={() => setShowConfirm(true)}
      />
      <Button
        type="ghost2"
        text={t('Cancel')}
        hasBottomSpacing={false}
        style={spacings.mtSm}
        onPress={handleClose}
      />
    </View>
  )
}

export default React.memo(ForgotPasswordModal)
